import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
} from "@mui/material";
import toast from "react-hot-toast";
import {
  fetchRolePermissionCatalog,
  rolesForApplication,
} from "./rolePermissionShared";
import Layout4 from "../DataLayouts/Layout4";
import TableDataGeneric from "../../Configurations/TableDataGeneric";
import ListSummaryCards from "../../Components/List/ListSummaryCards";

const BASE_ROUTE = "/role-permissions/access";

export const ROLE_COLUMNS = [
  {
    field: "user_role_name",
    label: "Role",
    visible: true,
    width: 280,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

export default function RolePermissionsList() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [appTab, setAppTab] = useState(0);

  const applications = catalog?.applications || [];
  const selectedApp = applications[appTab] || null;
  const applicationId = selectedApp?.application_id;

  const roles = useMemo(
    () => rolesForApplication(catalog || {}, applicationId),
    [catalog, applicationId]
  );

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRolePermissionCatalog();
      setCatalog(data);
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to load role permissions."
      );
      setCatalog(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `${BASE_ROUTE}/edit/${item.application_user_role_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `${BASE_ROUTE}/view/${item.application_user_role_id}`
      );
    },
    [navigate]
  );

  const summaryCards = useMemo(
    () => [
      {
        key: "apps",
        label: "Applications",
        value: applications.length,
        iconKey: "apps",
      },
      {
        key: "roles",
        label: "Roles for app",
        value: roles.length,
        iconKey: "shield",
      },
    ],
    [applications.length, roles.length]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading="Role permissions"
        subtitle="Select an application to see its roles, then view or edit permissions."
        Route={BASE_ROUTE}
        btnName="Add role permission"
        addButtonDisabled={!applicationId}
        onAddBtClick={() =>
          navigate(`${BASE_ROUTE}/add?application_id=${applicationId}`)
        }
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : applications.length === 0 ? (
        <Alert severity="info">No applications found.</Alert>
      ) : (
        <>
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={appTab}
              onChange={(_, v) => setAppTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {applications.map((app) => (
                <Tab
                  key={app.application_id}
                  label={app.application_name || `App ${app.application_id}`}
                />
              ))}
            </Tabs>
          </Paper>

          <ListSummaryCards cards={summaryCards} />

          {roles.length === 0 ? (
            <Alert severity="warning">
              No roles for this application. Assign roles under{" "}
              <strong>User roles access</strong> first.
            </Alert>
          ) : (
            <TableDataGeneric
              tableName="application_user_roles"
              primaryKey="application_user_role_id"
              heading="Role permissions"
              data={roles}
              sortname="user_role_name"
              showActions={true}
              Route={BASE_ROUTE}
              EditFunc={handleEdit}
              handleShow={handleShow}
              token={localStorage.getItem("token")}
              configss={ROLE_COLUMNS}
            />
          )}
        </>
      )}
    </>
  );
}
